import { css, html, LitElement, nothing } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
export interface WorkCredit {
  role: string
  name: string
  href?: string
}

@safeCustomElement('mdc-work-detail-card')
export class MdcWorkDetailCard extends LitElement {
  @property({ type: String, attribute: 'image-src' }) imageSrc = ''
  @property({ type: String, attribute: 'image-alt' }) imageAlt = ''
  @property({ type: String, attribute: 'card-title' }) cardTitle = ''
  @property({ type: String, attribute: 'campaign-type' }) campaignType = ''
  @property({ type: String, attribute: 'campaign-name' }) campaignName = ''
  @property({ type: String }) href = ''
  @property({ attribute: false }) pictureCredits: WorkCredit[] = []
  @property({ attribute: false }) workCredits: WorkCredit[] = []

  static styles = css`
    :host {
      display: block;
      font-family: var(--typography-font-family-sans);
      max-width: 20rem;
    }

    .mdc-work-detail-card {
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-075, 0.75rem);
      border: 1px solid var(--color-border-primary, var(--color-functional-gray-200));
      border-radius: var(--radius-md, 0.5rem);
      overflow: hidden;
      background-color: var(--color-functional-gray-white, #fff);
    }

    .mdc-work-detail-card__media {
      display: block;
      text-decoration: none;
    }

    .mdc-work-detail-card__media img {
      width: 100%;
      aspect-ratio: 4 / 3;
      object-fit: cover;
      display: block;
    }

    .mdc-work-detail-card__body {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-100);
      padding: 0 var(--spacing-100) var(--spacing-100);
    }

    .mdc-work-detail-card__title {
      font-size: var(--typography-font-size-100, 1rem);
      font-weight: 600;
      line-height: 1.3;
      margin: 0;
      color: var(--color-functional-gray-black);
    }

    .mdc-work-detail-card__title a {
      color: inherit;
      text-decoration: none;
    }

    .mdc-work-detail-card__title a:hover {
      text-decoration: underline;
    }

    .mdc-work-detail-card__title a:focus-visible {
      outline: 2px solid var(--color-border-focus, var(--color-functional-gray-black));
      outline-offset: 2px;
      text-decoration: underline;
    }

    .mdc-work-detail-card__subtitles {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-025, 0.25rem);
    }

    .mdc-work-detail-card__subtitle {
      overflow: hidden;
      color: var(--color-foreground-secondary, var(--color-functional-gray-400));
      text-overflow: ellipsis;
      max-width: 100%;
      white-space: nowrap;
      font-size: var(--typography-font-size-075, 0.75rem);
      font-weight: 450;
      line-height: 1.3;
      margin: 0;
      padding: 0;
    }

    .mdc-work-detail-card__subtitle--type {
      font-size: var(--typography-font-size-062-and-half, 0.625rem);
      text-transform: uppercase;
      letter-spacing: 0.03125rem;
    }

    .mdc-work-detail-card__subtitle--name {
      font-weight: 500;
      color: var(--color-foreground-primary, var(--color-functional-gray-500));
    }

    .mdc-work-detail-card__credits-heading {
      font-size: var(--typography-font-size-062-and-half);
      font-weight: 450;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--color-functional-gray-500, #6b7280);
      margin: 0 0 var(--spacing-050, 0.50rem);
    }

    .mdc-work-detail-card__credits-list {
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-012-and-half);
    }

    .mdc-work-detail-card__credit {
      display: flex;
      gap: var(--spacing-025, 0.25rem);
      font-size: var(--typography-font-size-075);
      line-height: 1.4;
    }

    .mdc-work-detail-card__credit dt {
      color: var(--color-functional-gray-500, #6b7280);
      white-space: nowrap;
    }

    .mdc-work-detail-card__credit dt::after {
      content: ':';
    }

    .mdc-work-detail-card__credit dd {
      margin: 0;
      color: var(--color-functional-gray-black);
    }

    .mdc-work-detail-card__credit dd a {
      color: inherit;
      text-decoration: none;
    }

    .mdc-work-detail-card__credit dd a:hover {
      text-decoration: underline;
    }

    .mdc-work-detail-card__credit dd a:focus-visible {
      outline: 2px solid var(--color-border-focus, var(--color-functional-gray-black));
      outline-offset: 2px;
      text-decoration: underline;
    }
  `

  private _renderCreditsSection(label: string, credits: WorkCredit[]) {
    if (!credits.length) return nothing

    return html`
      <section aria-label=${label}>
        <h4 class="mdc-work-detail-card__credits-heading">${label}</h4>
        <dl class="mdc-work-detail-card__credits-list">
          ${credits.map(
            (c) => html`
              <div class="mdc-work-detail-card__credit">
                <dt>${c.role}</dt>
                <dd>${c.href ? html`<a href=${c.href}>${c.name}</a>` : c.name}</dd>
              </div>
            `
          )}
        </dl>
      </section>
    `
  }

  render() {
    return html`
      <article class="mdc-work-detail-card">
        ${
          this.imageSrc
            ? this.href
              ? html`
              <a class="mdc-work-detail-card__media" href=${this.href || nothing}>
                <img src=${this.imageSrc} alt=${this.imageAlt} loading="lazy" />
              </a>
            `
              : html`
              <div class="mdc-work-detail-card__media">
                <img src=${this.imageSrc} alt=${this.imageAlt} loading="lazy" />
              </div>
            `
            : nothing
        }
        <div class="mdc-work-detail-card__body">
          <h3 class="mdc-work-detail-card__title">
            ${this.href ? html`<a href=${this.href}>${this.cardTitle}</a>` : this.cardTitle}
          </h3>
          ${
            this.campaignType || this.campaignName
              ? html`<div class="mdc-work-detail-card__subtitles">
                ${this.campaignType ? html`<p class="mdc-work-detail-card__subtitle mdc-work-detail-card__subtitle--type">${this.campaignType}</p>` : nothing}
                ${this.campaignName ? html`<p class="mdc-work-detail-card__subtitle mdc-work-detail-card__subtitle--name">${this.campaignName}</p>` : nothing}
              </div>`
              : nothing
          }
          ${this._renderCreditsSection('Picture Credits', this.pictureCredits)}
          ${this._renderCreditsSection('Work Credits', this.workCredits)}
        </div>
      </article>
    `
  }
}
